/**
 * Card — surface wrapper used by every tile/panel in the app.
 *
 * Three tones (default / highlight / muted) and three padding sizes. Polymorphic
 * via `as`. All visual values come from design tokens — see Card.module.css.
 */

import { forwardRef, type ElementType, type HTMLAttributes, type ReactNode } from 'react';
import styles from './Card.module.css';

type CardTone = 'default' | 'highlight' | 'muted';
type CardPadding = 'sm' | 'md' | 'lg';

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  tone?: CardTone;
  padding?: CardPadding;
  as?: ElementType;
  children: ReactNode;
}

const TONE_CLASS: Record<CardTone, string | undefined> = {
  default: styles.toneDefault,
  highlight: styles.toneHighlight,
  muted: styles.toneMuted,
};

const PADDING_CLASS: Record<CardPadding, string | undefined> = {
  sm: styles.paddingSm,
  md: styles.paddingMd,
  lg: styles.paddingLg,
};

export const Card = forwardRef<HTMLElement, CardProps>(function Card(
  { tone = 'default', padding = 'md', as: Tag = 'div', className, children, ...rest },
  ref,
) {
  const composedClassName = [styles.card, TONE_CLASS[tone], PADDING_CLASS[padding], className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <Tag ref={ref} className={composedClassName} {...rest}>
      {children}
    </Tag>
  );
});
